import React from "react"
import L10n from "lib/carrot-l10n"
import { H2, Body, Container, FlexContainer } from "@carrotfertility/carotene"
import { useProgressTracker } from "../../../views/modal/ProgressTracker"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Steps } from "../../workflow/steps"
import { BenefitEnrollmentModalContinueButton } from "../../shared"
import styled from "styled-components"
import { HR } from "../../../views/atoms/Atoms"
import { useUserDeductible } from "../../../context/user/DeductibleContext"
import Helpers from "../../../../utils/Helpers"
import { useSelector } from "react-redux"
import { getCurrencySymbol } from "../../../../reducers/companyInfo"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

interface DeductibleTableProps {
  partner?: boolean
}

const DeductibleTable = styled.div<DeductibleTableProps>`
  padding-right: 116px;
  margin-bottom: 24px;
  width: 504px;
  height: ${(props) => (props.partner ? "115px" : "74px")};
  background: #faf9f7;
  border-radius: 8px;
`

export function MemberOnlyOrCombinedDeductibleStatusStep(): JSX.Element {
  const { setProgress } = useProgressTracker()
  const { setNextStep } = useStateMachine(Steps.MEMBER_ONLY_OR_COMBINED_DEDUCTIBLE_STATUS, null)
  const { deductible } = useUserDeductible()
  const currencySymbol = useSelector(getCurrencySymbol)
  setProgress(1, "YOUR INSURANCE COVERAGE")

  return (
    <div>
      <H2
        stack="medium"
        variant="dark"
      >{l`Based on what you’ve told us, it looks like you have a deductible through Carrot.`}</H2>
      <DeductibleTable>
        <FlexContainer padding="medium" justifyContent="space-between">
          <Body color={"black-80"}>{l`Your annual deductible`}</Body>
          <Body variant="alt" color={"black-80"}>
            {Helpers.convertToCurrencyString(deductible.deductibleSnapshot?.totalAmount, currencySymbol, 0)}
          </Body>
        </FlexContainer>
      </DeductibleTable>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`Once it's met, qualified medical expenses will be eligible for reimbursement. 
        Other types of expenses are always eligible.`}</Body>
      </Container>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`Next, connect to your insurance provider so we can apply any payments you make 
        toward your medical plan deductible to this one as well.`}</Body>
      </Container>
      <BenefitEnrollmentModalContinueButton
        onClick={() => setNextStep(Steps.MEMBER_ONLY_OR_COMBINED_DEDUCTIBLE_STATUS, "")}
      />
    </div>
  )
}

export function PartnerOnlyDeductibleStatusStep(): JSX.Element {
  const { setProgress } = useProgressTracker()
  const { setNextStep } = useStateMachine(Steps.PARTNER_ONLY_DEDUCTIBLE_STATUS, null)
  const { partnerDeductible } = useUserDeductible()
  const currencySymbol = useSelector(getCurrencySymbol)
  setProgress(1, "YOUR INSURANCE COVERAGE")

  return (
    <div>
      <H2
        stack="medium"
        variant="dark"
      >{l`Based on what you’ve told us, it looks like your partner has a deductible through Carrot.`}</H2>
      <DeductibleTable>
        <FlexContainer padding="medium" justifyContent="space-between">
          <Body color={"black-80"}>{l`Your annual deductible`}</Body>
          <Body variant="alt" color={"black-80"}>
            {Helpers.convertToCurrencyString(partnerDeductible.deductibleSnapshot?.totalAmount, currencySymbol, 0)}
          </Body>
        </FlexContainer>
      </DeductibleTable>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`Once it's met, qualified medical expenses will be eligible for reimbursement. 
        Other types of expenses are always eligible.`}</Body>
      </Container>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`Next, connect to your partner’s insurance provider so we can apply any payments 
        you make toward your medical plan deductible to this one as well.`}</Body>
      </Container>
      <BenefitEnrollmentModalContinueButton onClick={() => setNextStep(Steps.PARTNER_ONLY_DEDUCTIBLE_STATUS, "")} />
    </div>
  )
}

export function MemberPartnerDeductibleStatusStep(): JSX.Element {
  const { setProgress } = useProgressTracker()
  const { setNextStep } = useStateMachine(Steps.MEMBER_PARTNER_DEDUCTIBLE_STATUS, null)
  setProgress(1, "YOUR INSURANCE COVERAGE")
  const { deductible, partnerDeductible } = useUserDeductible()
  const currencySymbol = useSelector(getCurrencySymbol)

  return (
    <div>
      <H2
        stack="medium"
        variant="dark"
      >{l`Based on what you’ve told us, it looks like you and your partner have deductibles through Carrot.`}</H2>
      <DeductibleTable partner>
        <Container padding="medium">
          <FlexContainer stack="tiny" padding="none" justifyContent="space-between">
            <Body color={"black-80"}>{l`Your annual deductible`}</Body>
            <Body variant="alt" color={"black-80"}>
              {Helpers.convertToCurrencyString(deductible.deductibleSnapshot?.totalAmount, currencySymbol, 0)}
            </Body>
          </FlexContainer>
          <div style={{ width: "452px", color: "#CCCCCC", marginBottom: "8px" }}>
            <HR />
          </div>
          <FlexContainer padding="none" justifyContent="space-between">
            <Body color={"black-80"}>{l`Your partner's annual deductible`}</Body>
            <Body variant="alt" color={"black-80"}>
              {Helpers.convertToCurrencyString(partnerDeductible.deductibleSnapshot?.totalAmount, currencySymbol, 0)}
            </Body>
          </FlexContainer>
        </Container>
      </DeductibleTable>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`Once they're met, qualified medical expenses will be eligible for reimbursement. 
        Other types of expenses are always eligible.`}</Body>
      </Container>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`Next, connect to your insurance provider and your partner's so we can apply any 
        payments you make toward your medical plan deductibles to these as well.`}</Body>
      </Container>
      <BenefitEnrollmentModalContinueButton onClick={() => setNextStep(Steps.MEMBER_PARTNER_DEDUCTIBLE_STATUS, "")} />
    </div>
  )
}
